import { InvestmentPhase, Property, PropertyPrice, PropertyPricePayload } from '@repo/services';
import { highValueDealThreshold, iecApprovalThreshold } from './aimConstants';

/**
 * Phase/status values that mean "closed". Intentionally duplicated from
 * `aimStatusUtils.completedStatuses` rather than imported: `aimStatusUtils`
 * pulls in `@repo/services` status helpers, and importing it here creates a
 * module cycle through the package barrel that breaks named exports from this
 * file. Keep the two lists in sync.
 */
const COMPLETED_PRICING_STATUSES = ['completed', 'complete', 'postclose'];


/**
 * Minimal structural shape of a property these price/acreage helpers actually need —
 * `isCanceled`, `localCurrency`, `acreage`/`tillableAcres`/`irrigatedAcres`, and
 * `propertyPrices[].amount`. `Property` satisfies this, but so does the PRM-520
 * `AdaptedProperty` view of a `TransactionTableRow`'s `properties[]` entry (see
 * `apps/prism2/.../TransactionTable/utils/transactionTableAdapter.ts`), whose
 * `propertyPrices[]` entries carry no `propertyId` (never returned by the Transaction Table
 * endpoint) and so aren't assignable to the full `PropertyPrice[]`.
 */
export type PriceableProperty = Pick<Property, 'isCanceled' | 'localCurrency' | 'acreage' | 'tillableAcres' | 'irrigatedAcres' | 'country'> & {
  propertyPrices?: { amount?: number }[];
};

/**
 * Minimal structural shape these price/acreage helpers actually need off a transaction —
 * `PrismTransaction` satisfies this, but so does the PRM-520 `AdaptedTransaction` view of a
 * `TransactionTableRow` (see `apps/prism2/.../TransactionTable/utils/transactionTableAdapter.ts`),
 * which doesn't carry every `PrismTransaction` field (e.g. no `prismTransactionChangeLogs`).
 * Widening these functions' parameter types to this shape (rather than requiring the full
 * `PrismTransaction`) lets both callers pass their own transaction object as-is.
 */
export interface PriceableTransaction {
  primaryCurrency?: string;
  properties?: PriceableProperty[];
  finalExchangeRate?: number;
  /** Settled price captured at/near closing. Preferred over summed property prices for COMPLETED transactions. */
  finalPurchasePrice?: number;
  /** ISO currency `finalPurchasePrice` is denominated in. Falls back to `primaryCurrency`. */
  finalPurchasePriceCurrency?: string;
  areaUnit?: string;
  /** Status fields used to detect the completed phase (structurally compatible with `PrismTransaction`). */
  isCanceled?: boolean;
  status?: string;
  investmentPhase?: { value?: string };
  checklistMilestone?: { value?: string };
}

/**
 * Strips the `id` field from each PropertyPrice so the array can be sent as a creation payload.
 *
 * @param transactionId - ID of the transaction the prices belong to (currently unused in the mapping).
 * @param prices - Property prices to convert into payload objects.
 * @returns Array of PropertyPricePayload objects with `id` removed.
 */
export const prepareTransactionPrices = (transactionId: number, prices: PropertyPrice[]) => {
  const payload: PropertyPricePayload[] = prices.map(({ id, ...rest }) => rest);
  return payload;
}

/**
 * Diffs an original and updated list of property prices to determine which need to be created, updated, or deleted.
 *
 * @param original - The previously saved property prices, if any.
 * @returns An object with `pricesToPost`, `pricesToPut`, and `pricesToDelete` arrays.
 */
export const getTransactionPriceChanges = (
  original?: PropertyPrice[],
  updated?: PropertyPrice[]
) => {
  let pricesToPut: PropertyPrice[] = [];
  let pricesToPost: PropertyPrice[] = [];
  let pricesToDelete: PropertyPrice[] = [];
  if (original && updated) {
    pricesToPost = updated.filter(
      (u) => !original.some((o) => o.id === u.id)
    );

    pricesToPut = updated.filter((u) => {
      const originalMatch = original.find((o) => o.id === u.id);
      return (
        originalMatch &&
        (originalMatch.amount !== u.amount ||
          originalMatch.transactionPriceTypeId !== u.transactionPriceTypeId)
      );
    });

    pricesToDelete = original.filter(
      (o) => !updated.some((u) => u.id === o.id)
    );
  }

  return { pricesToPost, pricesToPut, pricesToDelete };
};

/**
 * Returns true when the transaction involves any non-USD currency
 * (either at the transaction level via `primaryCurrency`, or at the property
 * level via `localCurrency`). USD-only transactions don't need FX context.
 */
export const isInternationalTransaction = (transaction: PriceableTransaction | undefined): boolean => {
  if (!transaction) return false;
  const primary = transaction.primaryCurrency?.toUpperCase();
  if (primary && primary !== 'USD') return true;
  return (transaction.properties ?? []).some((p) => {
    const local = p?.localCurrency?.toUpperCase();
    return !!local && local !== 'USD';
  });
};

/**
 * Returns true when the transaction has at least one property and every
 * property is located in the United States (country code 'US').
 * Used to suppress the foreign-exchange phase task for domestic transactions.
 */
export const isUsDomesticTransaction = (transaction: PriceableTransaction | undefined): boolean => {
  if (!transaction) return false;
  const props = transaction.properties ?? [];
  if (props.length === 0) return false;
  return props.every((p) => p?.country?.toUpperCase() === 'US');
};

/**
 * Returns an FX rates map (currency code → rateToUsd) where the transaction's
 * `primaryCurrency` is overridden with `transaction.finalExchangeRate` when
 * that field has been set (PRM-434). When the transaction has not captured a
 * final FX rate, the original map is returned unchanged.
 *
 * `finalExchangeRate` semantics: "USD per 1 unit of primaryCurrency" — the
 * same direction as the existing `rateToUsd` values, so it can be substituted
 * 1:1.
 */
export const getEffectiveFxRates = (
  transaction: PriceableTransaction | undefined,
  fxRates?: Record<string, number>
): Record<string, number> | undefined => {
  if (!transaction) return fxRates;
  const finalRate = transaction.finalExchangeRate;
  if (!finalRate || finalRate <= 0) return fxRates;
  const primary = transaction.primaryCurrency?.toUpperCase();
  if (!primary || primary === 'USD') return fxRates;
  return { ...(fxRates ?? {}), [primary]: finalRate };
};

/**
 * Converts a local-currency amount to USD using the provided FX rates map.
 * If no rate is found (or currency is USD), the amount is returned as-is.
 */
export const convertToUsd = (amount: number, currency: string, fxRates: Record<string, number>): number => {
  if (!currency || currency.toUpperCase() === 'USD') return amount;
  const rate = fxRates[currency];
  if (!rate || rate === 0) return amount; // fallback: treat as USD if rate unknown
  return amount * rate;
};

/**
 * Sums property prices across all NON-CANCELED properties, converting to USD using fxRates.
 * Canceled properties are always excluded so this value is consistent everywhere it's used
 * (transactions table, overview totals, IEC approval check, etc.).
 * @param fxRates - map of { [currencyCode]: rateToUsd } where rateToUsd is how many units of currency = 1 USD
 */
export const getEstPriceFromProperties = (properties: PriceableProperty[] | undefined, fxRates?: Record<string, number>) => {
  if (!properties) return undefined;
  let estPrice = 0;
  properties
    .filter((p) => !p?.isCanceled)
    .forEach((p) => {
      const currency = p.localCurrency || 'USD';
      const sum = p.propertyPrices?.reduce((acc, price) => acc + (price.amount || 0), 0) || 0;
      const sumUsd = fxRates ? convertToUsd(sum, currency, fxRates) : sum;
      estPrice += sumUsd;
    });
  return estPrice;
};

/**
 * Computes the estimated total USD price across a transaction's non-canceled properties, using the transaction's locked final exchange rate when set.
 *
 * @param transaction - The transaction whose properties should be summed.
 * @returns The estimated USD price, or undefined if the transaction has no properties.
 */
export const getEstPriceFromTransaction = (transaction: PriceableTransaction | undefined, fxRates?: Record<string, number>) => {
  return getEstPriceFromProperties(transaction?.properties, getEffectiveFxRates(transaction, fxRates));
};

/**
 * Structural equivalent of `isCompleted` from `aimStatusUtils`, expressed against
 * `PriceableTransaction` so this module stays usable by callers (e.g. the PRM-520
 * `AdaptedTransaction`) that don't carry a full `PrismTransaction`.
 */
const isCompletedForPricing = (transaction: PriceableTransaction | undefined): boolean => {
  if (!transaction) return false;
  if (transaction.isCanceled) return false;
  return (
    COMPLETED_PRICING_STATUSES.includes(transaction.status?.toLowerCase() ?? '') ||
    COMPLETED_PRICING_STATUSES.includes(transaction.investmentPhase?.value?.toLowerCase() ?? '') ||
    COMPLETED_PRICING_STATUSES.includes(transaction.checklistMilestone?.value?.toLowerCase() ?? '')
  );
};

/**
 * Authoritative USD price for a transaction, applying the completed-transaction
 * settlement rule.
 *
 * Rules:
 * 1. NON-completed transactions — sum the property `propertyPrices` and convert
 *    each property's `localCurrency` at live rates (unchanged legacy behaviour).
 * 2. COMPLETED transactions — prefer the settled `finalPurchasePrice` when
 *    present; otherwise fall back to the summed property prices. A price is
 *    ALWAYS produced, never null, so totals never silently drop a closed deal.
 * 3. FX — a captured `finalExchangeRate` always wins over the live rate (via
 *    {@link getEffectiveFxRates}). `finalPurchasePrice` is converted from
 *    `finalPurchasePriceCurrency`, falling back to `primaryCurrency`.
 *
 * `finalPurchasePrice` is only consulted for completed transactions: on an open
 * deal the field may be pre-populated/stale, and the in-flight property prices
 * remain the correct live estimate.
 *
 * @param transaction - The transaction to price.
 * @param fxRates - Live FX map of { [currencyCode]: rateToUsd }.
 * @returns The transaction's USD value.
 */
export const getTransactionPriceUsd = (
  transaction: PriceableTransaction | undefined,
  fxRates?: Record<string, number>
): number => {
  if (!transaction) return 0;

  const effectiveFxRates = getEffectiveFxRates(transaction, fxRates);
  const estPrice = getEstPriceFromProperties(transaction.properties, effectiveFxRates) ?? 0;

  if (!isCompletedForPricing(transaction)) return estPrice;

  const finalPrice = transaction.finalPurchasePrice;
  // Treat null/undefined/0 as "not captured" and fall back to the estimate —
  // a completed deal must always contribute a value to the totals.
  if (!finalPrice || finalPrice <= 0) return estPrice;

  const currency = (transaction.finalPurchasePriceCurrency || transaction.primaryCurrency || 'USD').toUpperCase();
  return effectiveFxRates ? convertToUsd(finalPrice, currency, effectiveFxRates) : finalPrice;
};

/**
 * USD value of a SINGLE property, consistent with {@link getTransactionPriceUsd}
 * at the transaction level.
 *
 * Needed by breakdowns that attribute value to a location/asset type per property
 * (e.g. the "Transactions by Location" chart). `finalPurchasePrice` is captured at
 * the transaction level only, so when it applies it is allocated across the
 * transaction's non-canceled properties **pro rata** by each property's share of
 * the estimated property-price total. This guarantees the per-property figures
 * sum back to `getTransactionPriceUsd`, so a chart's bars always reconcile with
 * the dashboard cards.
 *
 * When the estimated total is 0 (no property prices entered) the settled price is
 * split evenly, so the value still appears somewhere rather than vanishing.
 *
 * @param transaction - The parent transaction (supplies phase, FX and settled price).
 * @param property - The property to value.
 * @param fxRates - Live FX map of { [currencyCode]: rateToUsd }.
 * @returns The property's USD value.
 */
export const getPropertyPriceUsd = (
  transaction: PriceableTransaction | undefined,
  property: PriceableProperty | undefined,
  fxRates?: Record<string, number>
): number => {
  if (!transaction || !property) return 0;

  const effectiveFxRates = getEffectiveFxRates(transaction, fxRates);
  const propertyEst = getEstPriceFromProperties([property], effectiveFxRates) ?? 0;

  // Only completed transactions with a captured settled price need allocating;
  // everything else uses the property's own estimate unchanged.
  const finalPrice = transaction.finalPurchasePrice;
  if (!isCompletedForPricing(transaction) || !finalPrice || finalPrice <= 0) return propertyEst;

  const settledTotal = getTransactionPriceUsd(transaction, fxRates);
  const estTotal = getEstPriceFromProperties(transaction.properties, effectiveFxRates) ?? 0;

  // Allocate pro rata by each property's share of the estimated total so the
  // per-property values sum back to the transaction total.
  if (estTotal > 0) return settledTotal * (propertyEst / estTotal);

  // No property prices entered — split evenly so the value still appears.
  const activeCount = (transaction.properties ?? []).filter((p) => !p?.isCanceled).length;
  if (activeCount === 0 || property.isCanceled) return 0;
  return settledTotal / activeCount;
};

/**
 * Returns true if the transaction has any properties with a price in a foreign currency
 * (i.e. a currency other than the transaction's primary currency).
 *
 * @param transaction - The transaction to check.
 * @returns True if any property has a foreign currency price, false otherwise.
 */
export const hasForeignCurrencyProperties = (transaction: PriceableTransaction | undefined): boolean => {
  if (!transaction) return false;
  const primaryCurrency = transaction.primaryCurrency?.toUpperCase();
  return (transaction.properties ?? []).some((property) => {
    const currency = property.localCurrency?.toUpperCase();
    return currency && currency !== primaryCurrency;
  });
};

/**
 * Determines whether a transaction is a "high value" deal (i.e. requires CEO
 * qualification) based on its estimated USD price meeting or exceeding the
 * high-value deal threshold — NOT the stored `highValueDeal` bit.
 *
 * @param transaction - The transaction to evaluate.
 * @param fxRates - Optional FX rate map for converting property prices to USD.
 * @returns True when the transaction's est USD price is at/above the threshold.
 */
export const isHighValueTransaction = (
  transaction: PriceableTransaction | undefined,
  fxRates?: Record<string, number>
): boolean => {
  if (!transaction) return false;
  const estPrice = getEstPriceFromTransaction(transaction, fxRates);
  return (estPrice ?? 0) >= highValueDealThreshold;
};

/**
 * Sums the acreage of all non-canceled properties on a transaction.
 *
 * @param transaction - The transaction whose property acreages should be summed.
 * @returns Total estimated acres across active properties.
 */
export const getEstAcresFromTransaction = (transaction: PriceableTransaction | undefined) => {
  const properties = transaction?.properties;
  let estAcres = 0;
  properties?.filter(p => !p.isCanceled).forEach((p) => {
    const acreage = p.acreage || 0;
    estAcres += acreage;
  });
  return estAcres;
}

/**
 * Determines whether IEC approval is required based on whether the estimated price of the given properties meets or exceeds the IEC approval threshold.
 *
 * @param properties - Properties to evaluate the estimated price for.
 * @returns True if the estimated price is at or above the IEC approval threshold; undefined if properties is not provided.
 */
export const isIECApprovalRequired = (properties: Property[] | undefined) => {
  if (!properties) return;
  const estPrice = getEstPriceFromProperties(properties);
  return (estPrice || 0) >= iecApprovalThreshold;
}

/**
 * Picks the appropriate price label to display based on the transaction's current investment phase.
 *
 * @param investmentPhase - The investment phase used to determine which label to show.
 * @returns A human-readable label such as "Recommended Offer Price", "Approved Price", "Negotiated Price", or "Closing Price".
 */
export const getPriceDetailsLabel = (investmentPhase: InvestmentPhase) => {
  let label = 'Recommended Offer Price';
  const val = investmentPhase?.value;
  if (val) {
    if (val?.toLowerCase() === 'eitapproved') label = 'Approved Price';
    else if (val?.toLowerCase() === 'undercontract') label = 'Negotiated Price';
    else if (val?.toLowerCase() === 'completed') label = 'Closing Price';
  }
  return label;
}

/**
 * Aggregates acreage and price totals across a transaction's non-canceled properties, converting property-level prices to the transaction's primary currency (and to USD) using the effective FX rates.
 *
 * @param transaction - The transaction whose properties should be aggregated.
 * @param fxRates - Optional FX rate map for converting property prices.
 * @returns An object with `totalTillableAcres`, `totalIrrigatedAcres`, `estAcres`, `estPrice` (in primary currency), `avgPricePerAcre`, and `grandTotalUsd`.
 */
export const getPropertyTotalsFromTransaction = (transaction: PriceableTransaction, fxRates?: Record<string, number>) => {
  // When the transaction has captured a Final Exchange Rate (PRM-434), prefer
  // that locked rate over the live FX rate for the primaryCurrency. All
  // downstream conversions in this function then see the locked rate.
  const effectiveFxRates = getEffectiveFxRates(transaction, fxRates);
  const properties = transaction?.properties;
  const activeProperties = properties?.filter((p) => !p?.isCanceled);
  const totalTillableAcres = activeProperties?.reduce((total, property) => total + (property.tillableAcres || 0), 0) || 0;
  const totalIrrigatedAcres = activeProperties?.reduce((total, property) => total + (property.irrigatedAcres || 0), 0) || 0;
  const estAcres = getEstAcresFromTransaction(transaction);

  const primaryCurrency = transaction?.primaryCurrency?.toUpperCase() || 'USD';
  const primaryCurrencyRateToUsd = primaryCurrency === 'USD' ? 1 : effectiveFxRates?.[primaryCurrency];

  const totalPriceInPrimaryCurrency = properties
    ?.filter((property) => !property?.isCanceled)
    .reduce((total, property) => {
      // Fall back to primaryCurrency when localCurrency is not set
      const currency = (property.localCurrency || primaryCurrency).toUpperCase();
      const propertyTotal = property.propertyPrices?.reduce((sum, price) => sum + (price.amount || 0), 0) || 0;

      if (currency === primaryCurrency) return total + propertyTotal;
      if (!effectiveFxRates) return total + propertyTotal;

      const propertyRateToUsd = currency === 'USD' ? 1 : effectiveFxRates[currency];

      if (!propertyRateToUsd || !primaryCurrencyRateToUsd) return total + propertyTotal;

      const propertyTotalUsd = propertyTotal * propertyRateToUsd;
      return total + (propertyTotalUsd / primaryCurrencyRateToUsd);
    }, 0) || 0;

  const avgPricePerAcre = totalTillableAcres > 0 ? totalPriceInPrimaryCurrency / totalTillableAcres : 0;

  const grandTotalUsd = properties
    ?.filter((property) => !property?.isCanceled)
    .reduce((total, property) => {
      // Fall back to primaryCurrency when localCurrency is not set
      const currency = (property.localCurrency || primaryCurrency).toUpperCase();
      const propertyTotal = property.propertyPrices?.reduce((sum, price) => sum + (price.amount || 0), 0) || 0;

      if (currency === 'USD') return total + propertyTotal;
      if (!effectiveFxRates) return total + propertyTotal;

      const rate = effectiveFxRates[currency];
      if (!rate) return total + propertyTotal;

      return total + (propertyTotal * rate);
    }, 0) || 0;

  return { totalTillableAcres, totalIrrigatedAcres, estAcres, estPrice: totalPriceInPrimaryCurrency, avgPricePerAcre, grandTotalUsd };
}


/**
 * Returns a map of { [currencyCode]: totalInLocalCurrency } across all properties.
 * Amounts are stored in the property's localCurrency, so they must NOT be naively summed as USD.
 * When localCurrency is not set, falls back to defaultCurrency (typically the transaction's primaryCurrency).
 */
export const getPropertyCurrencyTotals = (transaction: PriceableTransaction | undefined, defaultCurrency?: string): Record<string, number> => {
  const map: Record<string, number> = {};
  if (!transaction?.properties) return map;
  const fallback = defaultCurrency || transaction?.primaryCurrency || 'USD';
  for (const property of transaction.properties) {
    if (property.isCanceled) continue; // Exclude canceled properties
    const code = property.localCurrency || fallback;
    const total = property.propertyPrices?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
    map[code] = (map[code] || 0) + total;
  }
  return map;
}
